import { useState, useCallback } from "react";
import { SimCanvas } from "@/components/SimCanvas";
import { createBall, DEFAULT_CONFIG } from "@/lib/physics";
import type { Ball, PhysicsConfig } from "@/lib/physics";
import { Button } from "@/components/ui/button";

export function Lesson4Sandbox() {
	const [balls, setBalls] = useState<Ball[]>([]);
	const [config, setConfig] = useState<PhysicsConfig>({ ...DEFAULT_CONFIG });
	const [showTrails, setShowTrails] = useState(true);
	const [showVectors, setShowVectors] = useState(false);
	const [showEnergy, setShowEnergy] = useState(true);
	const [ballSize, setBallSize] = useState(20);

	const handleClick = useCallback(
		(x: number, y: number) => {
			const vx = (Math.random() - 0.5) * 500;
			const vy = (Math.random() - 0.5) * 300 - 200;
			setBalls((prev) => [...prev, createBall(x, y, vx, vy, ballSize)]);
		},
		[ballSize],
	);

	const spawnBurst = () => {
		const newBalls: Ball[] = [];
		for (let i = 0; i < 15; i++) {
			const angle = (Math.PI * 2 * i) / 15;
			const speed = 200 + Math.random() * 300;
			newBalls.push(
				createBall(
					350,
					200,
					Math.cos(angle) * speed,
					Math.sin(angle) * speed,
					8 + Math.random() * 18,
				),
			);
		}
		setBalls((prev) => [...prev, ...newBalls]);
	};

	const spawnRain = () => {
		const newBalls: Ball[] = [];
		for (let i = 0; i < 20; i++) {
			newBalls.push(
				createBall(
					50 + Math.random() * 600,
					-20 - Math.random() * 200,
					(Math.random() - 0.5) * 100,
					Math.random() * 100,
					6 + Math.random() * 14,
				),
			);
		}
		setBalls((prev) => [...prev, ...newBalls]);
	};

	return (
		<div className="space-y-4">
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<div className="space-y-3">
					<div className="flex items-center gap-3">
						<label className="text-xs font-medium min-w-[80px]">Gravity</label>
						<input
							type="range"
							min="0"
							max="3000"
							value={config.gravity}
							onChange={(e) => setConfig((p) => ({ ...p, gravity: Number(e.target.value) }))}
							className="flex-1 accent-pink-500"
						/>
						<span className="text-xs text-pink-400 min-w-[40px]">{(config.gravity / 100).toFixed(1)}</span>
					</div>
					<div className="flex items-center gap-3">
						<label className="text-xs font-medium min-w-[80px]">Bounce</label>
						<input
							type="range"
							min="0"
							max="100"
							value={config.restitution * 100}
							onChange={(e) => setConfig((p) => ({ ...p, restitution: Number(e.target.value) / 100 }))}
							className="flex-1 accent-cyan-400"
						/>
						<span className="text-xs text-cyan-400 min-w-[40px]">{(config.restitution * 100).toFixed(0)}%</span>
					</div>
					<div className="flex items-center gap-3">
						<label className="text-xs font-medium min-w-[80px]">Ball Size</label>
						<input
							type="range"
							min="5"
							max="50"
							value={ballSize}
							onChange={(e) => setBallSize(Number(e.target.value))}
							className="flex-1 accent-yellow-400"
						/>
						<span className="text-xs text-yellow-400 min-w-[40px]">{ballSize}px</span>
					</div>
					<div className="flex items-center gap-3">
						<label className="text-xs font-medium min-w-[80px]">Air Drag</label>
						<input
							type="range"
							min="900"
							max="1000"
							value={config.airResistance * 1000}
							onChange={(e) => setConfig((p) => ({ ...p, airResistance: Number(e.target.value) / 1000 }))}
							className="flex-1 accent-purple-400"
						/>
						<span className="text-xs text-purple-400 min-w-[40px]">{((1 - config.airResistance) * 100).toFixed(1)}%</span>
					</div>
				</div>
				<div className="space-y-2">
					<div className="flex flex-wrap gap-2">
						<Button variant="outline" size="sm" onClick={spawnBurst}>
							Explosion
						</Button>
						<Button variant="outline" size="sm" onClick={spawnRain}>
							Rain
						</Button>
						<Button variant="outline" size="sm" onClick={() => setBalls([])}>
							Clear All
						</Button>
					</div>
					<div className="flex flex-wrap gap-2">
						<Button
							variant="outline"
							size="sm"
							onClick={() => setShowTrails(!showTrails)}
							className={showTrails ? "border-cyan-400 text-cyan-400" : ""}
						>
							Trails
						</Button>
						<Button
							variant="outline"
							size="sm"
							onClick={() => setShowVectors(!showVectors)}
							className={showVectors ? "border-cyan-400 text-cyan-400" : ""}
						>
							Vectors
						</Button>
						<Button
							variant="outline"
							size="sm"
							onClick={() => setShowEnergy(!showEnergy)}
							className={showEnergy ? "border-cyan-400 text-cyan-400" : ""}
						>
							Energy
						</Button>
					</div>
					<p className="text-xs text-muted-foreground">
						Balls: <span className="text-cyan-400 font-mono">{balls.length}</span>
					</p>
				</div>
			</div>

			<SimCanvas
				balls={balls}
				config={config}
				showTrails={showTrails}
				showVectors={showVectors}
				showEnergy={showEnergy}
				onClick={handleClick}
			/>

			<p className="text-sm text-muted-foreground">
				Your personal physics playground! Click to spawn balls, tweak every parameter, and watch chaos unfold. Try the explosion button!
			</p>
		</div>
	);
}
